import { useEffect, useMemo, useState } from 'react';
import type { Tool, ToolGroup } from '@orqforge/shared';
import { deriveToolGroups } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

function readApiError(err: unknown): string {
  if (!(err instanceof Error) || !err.message.trim()) return 'Could not update agent access.';
  try {
    const body = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (Array.isArray(body.message) && body.message[0]) return body.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

export function AgentDetailTools({
  agentId,
  onError,
}: {
  agentId: string;
  onError: (message: string | null) => void;
}) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyTool, setBusyTool] = useState<string | null>(null);
  const [busyGroup, setBusyGroup] = useState<string | null>(null);
  const groups = useMemo(() => deriveToolGroups(tools), [tools]);

  useEffect(() => {
    void apiClient.listTools().then((r) => setTools(r.items)).catch(() => setTools([]));
    void apiClient.agentTools(agentId).then((rows) => setAssigned(new Set(rows.map((t) => t.id)))).catch(() => setAssigned(new Set()));
  }, [agentId]);

  function toggleExpand(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function fullyAssigned(group: ToolGroup) {
    return group.tools.length > 0 && group.tools.every((t) => assigned.has(t.id));
  }

  async function toggleTool(tool: Tool) {
    if (busyTool) return;
    const isAssigned = assigned.has(tool.id);
    onError(null);
    setBusyTool(tool.id);
    try {
      if (isAssigned) await apiClient.unassignTool(agentId, tool.id);
      else await apiClient.assignTool(agentId, tool.id);
      setAssigned((current) => {
        const next = new Set(current);
        if (isAssigned) next.delete(tool.id);
        else next.add(tool.id);
        return next;
      });
    } catch (err) {
      onError(readApiError(err));
    } finally {
      setBusyTool(null);
    }
  }

  async function toggleGroup(group: ToolGroup) {
    if (busyGroup) return;
    onError(null);
    setExpanded((current) => new Set(current).add(group.key));
    const revoke = fullyAssigned(group);
    const targets = revoke ? group.tools : group.tools.filter((t) => !assigned.has(t.id));
    if (targets.length === 0) return;
    setBusyGroup(group.key);
    try {
      await Promise.all(targets.map((t) => (revoke ? apiClient.unassignTool(agentId, t.id) : apiClient.assignTool(agentId, t.id))));
      setAssigned((current) => {
        const next = new Set(current);
        for (const t of targets) {
          if (revoke) next.delete(t.id);
          else next.add(t.id);
        }
        return next;
      });
    } catch (err) {
      onError(readApiError(err));
    } finally {
      setBusyGroup(null);
    }
  }

  return (
    <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Tool access</h2>
        <span className="text-xs text-gray-400">{assigned.size} of {tools.length} enabled</span>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Click a tool to grant or revoke this agent's access. Use Grant all to add an entire group. Tools required by assigned skills cannot be revoked until those skills are removed.
      </p>
      {groups.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No tools defined yet. Create them on the Tools page.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {groups.map((group) => {
            const open = expanded.has(group.key);
            const full = fullyAssigned(group);
            return (
              <div key={group.key} className={`rounded-xl border ${full ? 'border-blue-400' : 'border-gray-200 dark:border-gray-700'}`}>
                <div className="flex w-full items-center gap-3 px-3 py-2.5">
                  <button type="button" className="flex flex-1 items-center gap-3 text-left" onClick={() => toggleExpand(group.key)}>
                    <span className="text-[10px] text-gray-400">{open ? '▼' : '▶'}</span>
                    <span className="flex-1 text-sm font-medium">{group.label}</span>
                    <Badge variant="secondary">{group.tools.length}</Badge>
                  </button>
                  <Button size="sm" variant="ghost" disabled={busyGroup === group.key} onClick={() => void toggleGroup(group)}>
                    {busyGroup === group.key ? '...' : full ? 'Revoke all' : 'Grant all'}
                  </Button>
                </div>
                {open && (
                  <div className="border-t border-gray-200 px-3 py-2 dark:border-gray-700">
                    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
                      {group.tools.map((tool) => {
                        const on = assigned.has(tool.id);
                        return (
                          <button
                            key={tool.id}
                            type="button"
                            disabled={busyTool === tool.id || busyGroup === group.key}
                            className={`flex items-center gap-3 rounded-xl border p-3 text-left ${on ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600/60 dark:bg-blue-950/40' : 'border-gray-200 hover:border-blue-400 dark:border-gray-700'}`}
                            onClick={() => void toggleTool(tool)}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium">{tool.name}</span>
                              <span className="block truncate text-xs text-gray-400">{tool.key}</span>
                            </span>
                            <span className="text-xs text-blue-600">{busyTool === tool.id ? '...' : on ? 'On' : 'Add'}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
